/**
 * Photo viewing.
 *
 * ## The route this file used to be missing
 *
 * This module was written when the API had no way to hand back a viewable photo, and it said
 * so honestly rather than rendering a URL that 404s: `mediaSrc` returned null and every tile
 * fell back to a labelled placeholder. That route has since landed — `GET /media/:id` in
 * apps/api/src/routes/media.ts — so the placeholder is no longer honesty, it is just a lie in
 * the other direction. The console's whole reason to exist is showing the owner the evidence.
 *
 * ## Why a ticket and not the bearer token
 *
 * The caller here is an `<img src>`, which cannot send an `Authorization` header. So the API
 * mints a short-lived, GET-only media ticket (`POST /media/ticket`) that rides in the query
 * string, and `requireAuthOrTicket` accepts it in place of the bearer. Ten minutes, and a
 * ticket cannot mint its successor — only a real session can — so a forwarded photo URL stops
 * working long before it stops being someone's evidence.
 *
 * ## VITE_MEDIA_BASE_URL
 *
 * Still honoured, and still the seam for a deployment that fronts R2 with a public bucket or a
 * CDN: set it and tiles resolve to `${BASE}/${r2Key}` with no ticket involved. It is unset by
 * default, and should stay unset for anything with real customers in it — a public bucket URL
 * for a job photo is a public bucket URL for a job photo, forever, to anyone who has it. The
 * ticketed API route is the private path and it is the default.
 */

import { useEffect, useState } from "react";
import { api } from "./api.js";
import type { MediaRow } from "./types.js";

const PUBLIC_BASE = (import.meta.env.VITE_MEDIA_BASE_URL as string | undefined)?.replace(/\/$/, "");
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

interface TicketResponse {
  ticket: string;
  expires_in: number;
}

let cached: { ticket: string; expiresAt: number } | null = null;
let inflight: Promise<string | null> | null = null;

/**
 * One ticket per console session, reused by every tile on the screen.
 *
 * Single-flight for the same reason `refreshTokens` is: a job with twelve photographs mounts
 * twelve tiles in one tick, and twelve POSTs to mint twelve interchangeable tickets is a
 * self-inflicted thundering herd. Renewed a minute early so a tile that mounts on the boundary
 * does not get a ticket that expires between the URL being built and the `<img>` fetching it.
 */
export async function mediaTicket(): Promise<string | null> {
  if (PUBLIC_BASE) return null;
  if (cached && Date.now() < cached.expiresAt - 60_000) return cached.ticket;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await api<TicketResponse>("/media/ticket", { method: "POST" });
      cached = { ticket: res.ticket, expiresAt: Date.now() + res.expires_in * 1000 };
      return res.ticket;
    } catch {
      // A tile that cannot get a ticket renders its placeholder, which is the same thing it
      // did for a year. It is not worth breaking the job screen over.
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** Null until the ticket lands; tiles render their placeholder for that one tick. */
export function useMediaTicket(): string | null {
  const [ticket, setTicket] = useState<string | null>(cached?.ticket ?? null);

  useEffect(() => {
    let alive = true;
    void mediaTicket().then((t) => {
      if (alive) setTicket(t);
    });
    return () => {
      alive = false;
    };
  }, []);

  return ticket;
}

/**
 * The viewable URL for a photo, or null when there is none to be had.
 *
 * Null is returned for anything not `stored` even when the route is live: a `pending` row's
 * blob is still in OPFS on a phone, and its r2Key is a key for an object that does not exist
 * yet. Rendering an <img> at it would show the manager a broken image where the truth is "the
 * photo exists, it just hasn't arrived". SYNC.md Rule 3.
 */
export function mediaSrc(
  m: Pick<MediaRow, "id" | "uploadState" | "r2Key">,
  ticket?: string | null,
): string | null {
  if (m.uploadState !== "stored" || !m.r2Key) return null;
  if (PUBLIC_BASE) return `${PUBLIC_BASE}/${m.r2Key}`;
  if (!ticket) return null;
  return `${API_BASE}/media/${m.id}?t=${encodeURIComponent(ticket)}`;
}

/**
 * Whether this deployment can show photos at all — now a property of the build, since the API
 * route is unconditional and the ticket needs no configuration. Kept as an export because the
 * pages still ask, and because a deployment pointed at an older API would want it back.
 */
export const MEDIA_VIEWING_CONFIGURED = true;
